import React, { useState, useEffect } from "react";
import { usePreferences } from "../../hooks/usePreferences";
import {
  getSimulationService,
  SIMULATION_ROUTES,
} from "../../services/simulationService";
import { authService } from "../../services/authService";
import debugx from "debugx";
import { ConfirmationDialog } from "../Activities/ConfirmationDialog";

const debug = debugx("bikedash:debug-tab");

// Import sub-components that will be shared
import { CacheManagementSection } from "./CacheManagementSection";
import { SuppressedPatternsList } from "./SuppressedPatternsList";
import { SettingRow } from "./SettingRow";

export function DebugTab() {
  const { userPreferences, updateUserPreferences } = usePreferences();
  const [debugConfig, setDebugConfig] = useState(
    localStorage.getItem("debug") || "",
  );
  const [showDebugOverlay, setShowDebugOverlay] = useState(
    localStorage.getItem("showDebugOverlay") === "true",
  );
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);

  // Simulation state
  const simulationService = getSimulationService();

  // Trigger lazy loading of routes when Debug tab mounts
  useEffect(() => {
    debug("Debug tab mounted - triggering lazy route load");
    simulationService.getAvailableRoutes();
  }, [simulationService]);
  const [simulationState, setSimulationState] = useState(() =>
    simulationService.getState(),
  );
  const [simulationProgress, setSimulationProgress] = useState(() =>
    simulationService.getProgress(),
  );
  const [selectedRoute, setSelectedRoute] = useState(
    SIMULATION_ROUTES[0]?.id || "",
  );
  const [simOptions, setSimOptions] = useState(() => {
    return simulationService.getOptions();
  });

  // Update simulation progress periodically when running
  useEffect(() => {
    if (simulationState.isActive && !simulationState.isPaused) {
      const interval = setInterval(() => {
        setSimulationProgress(simulationService.getProgress());
      }, 200);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [simulationState.isActive, simulationState.isPaused]);

  const saveDebugConfig = (value: string) => {
    if (value.trim()) {
      localStorage.setItem("debug", value.trim());
    } else {
      localStorage.removeItem("debug");
    }
    debug("Debug config updated:", value);
  };

  // Simulation handlers
  const handleStartSimulation = async () => {
    if (!selectedRoute) return;

    try {
      simulationService.setRoute(selectedRoute);
      simulationService.setOptions(simOptions);

      if (await simulationService.start()) {
        setSimulationState(simulationService.getState());
        setSimulationProgress(simulationService.getProgress());
        debug("GPS simulation started with route:", selectedRoute);
      }
    } catch (error) {
      debug.error("Failed to start simulation:", error);
    }
  };

  const handleStopSimulation = async () => {
    try {
      await simulationService.stop();
      setSimulationState(simulationService.getState());
      setSimulationProgress(simulationService.getProgress());
      debug("GPS simulation stopped");
    } catch (error) {
      debug.error("Failed to stop simulation:", error);
    }
  };

  const handlePauseSimulation = () => {
    simulationService.pause();
    setSimulationState(simulationService.getState());
    debug("GPS simulation toggled");
  };

  const handleResetSimulation = async () => {
    try {
      await simulationService.reset();
      setSimulationState(simulationService.getState());
      setSimulationProgress(simulationService.getProgress());
      debug("GPS simulation reset");
    } catch (error) {
      debug.error("Failed to reset simulation:", error);
    }
  };

  const updateSimOption = <K extends keyof typeof simOptions>(
    key: K,
    value: (typeof simOptions)[K],
  ) => {
    const newOptions = { ...simOptions, [key]: value };
    setSimOptions(newOptions);
    simulationService.setOptions(newOptions);
    debug("Simulation option updated:", key, "=", value);
  };

  const handleClearAllData = async () => {
    try {
      debug("Clearing all application data...");

      // Logout first if logged in
      try {
        await authService.logout();
        debug("User logged out");
      } catch (error) {
        debug.warn("Failed to logout:", error);
      }

      // Clear all localStorage
      localStorage.clear();
      debug("localStorage cleared");

      // Clear all sessionStorage
      sessionStorage.clear();
      debug("sessionStorage cleared");

      // Clear IndexedDB databases
      if ("indexedDB" in window) {
        const databases = await indexedDB.databases();
        for (const db of databases) {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
            debug("Deleted IndexedDB:", db.name);
          }
        }
      }

      // Clear OPFS (Origin Private File System)
      if ("storage" in navigator && "getDirectory" in navigator.storage) {
        try {
          const root = await navigator.storage.getDirectory();
          // @ts-ignore - removeEntry is available but types might be incomplete
          for await (const entry of root.values()) {
            await root.removeEntry(entry.name, { recursive: true });
            debug("Deleted OPFS entry:", entry.name);
          }
          debug("OPFS cleared");
        } catch (error) {
          debug.warn("Failed to clear OPFS:", error);
        }
      }

      // Clear all caches
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          await caches.delete(cacheName);
          debug("Deleted cache:", cacheName);
        }
        debug("All caches cleared");
      }

      debug("All data cleared successfully. Reloading...");

      // Reload the page to start fresh
      window.location.reload();
    } catch (error) {
      debug.error("Error clearing all data:", error);
      alert("Error clearing data. Please check console for details.");
    }
  };

  return (
    <div className="settings-section">
      {/* Debug Console Section */}
      <div className="settings-group-header">Debug Console</div>

      <SettingRow
        label="Show debug overlay"
        description="Display debug console overlay on map"
        checked={showDebugOverlay}
        onChange={(checked) => {
          setShowDebugOverlay(checked);
          localStorage.setItem("showDebugOverlay", checked.toString());
          window.dispatchEvent(
            new StorageEvent("storage", {
              key: "showDebugOverlay",
              newValue: checked.toString(),
            }),
          );
          debug("Debug overlay toggled to:", checked);
        }}
      />

      <SettingRow
        label="Show cache grid overlay"
        description="Display cached grid squares on map with light blue overlay"
        checked={localStorage.getItem("showCacheGrid") === "true"}
        onChange={(checked) => {
          localStorage.setItem("showCacheGrid", checked.toString());
          window.dispatchEvent(
            new StorageEvent("storage", {
              key: "showCacheGrid",
              newValue: checked.toString(),
            }),
          );
          debug("Cache grid overlay toggled to:", checked);
        }}
      />

      <div className="setting-group">
        <label>Debug Configuration</label>
        <p className="setting-description">
          Configure debug logging namespaces. Examples: 'bikedash:*',
          'bikedash:map,bikedash:gps'
        </p>
        <div style={{ marginTop: "12px" }}>
          <textarea
            placeholder="Enter debug namespaces (e.g., bikedash:*)"
            value={debugConfig}
            onChange={(e) => {
              const value = e.target.value;
              setDebugConfig(value);
              saveDebugConfig(value);
            }}
            style={{
              width: "100%",
              minHeight: "60px",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid var(--border)",
              background: "var(--bg)",
              color: "var(--text)",
              fontSize: "12px",
              fontFamily: "monospace",
              resize: "vertical",
            }}
          />
        </div>
      </div>

      <div className="setting-group">
        <label>Suppressed Log Patterns</label>
        <p className="setting-description">
          Manage suppressed namespaces and templates. Right-click or long-press
          log messages in the debug console to add suppressions.
        </p>
        <div
          style={{
            marginTop: "12px",
            background: "rgba(255,255,255,0.05)",
            borderRadius: "4px",
            padding: "8px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          <SuppressedPatternsList />
        </div>
      </div>

      {/* GPS Simulation Section */}
      <div className="settings-group-header" style={{ marginTop: "32px" }}>
        GPS Simulation
      </div>

      <div className="setting-row">
        <div className="setting-info">
          <div className="setting-label">GPS Simulation Status</div>
          <div className="setting-description">
            {simulationState.isActive
              ? `${simulationState.isPaused ? "Paused" : "Running"} - ${SIMULATION_ROUTES.find((r) => r.id === selectedRoute)?.name || "Unknown Route"}`
              : "Not running"}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            alignItems: "center",
          }}
        >
          {!simulationState.isActive ? (
            <button
              className="control-btn primary"
              onClick={handleStartSimulation}
              disabled={!selectedRoute}
            >
              Start
            </button>
          ) : (
            <>
              <button
                className={`control-btn ${simulationState.isPaused ? "primary" : ""}`}
                onClick={handlePauseSimulation}
              >
                {simulationState.isPaused ? "Resume" : "Pause"}
              </button>
              <button
                className="control-btn stop"
                onClick={handleStopSimulation}
              >
                Stop
              </button>
              <button className="control-btn" onClick={handleResetSimulation}>
                Reset
              </button>
            </>
          )}
        </div>
      </div>

      <div className="setting-group">
        <label>Simulation Route</label>
        <select
          value={selectedRoute}
          onChange={(e) => setSelectedRoute(e.target.value)}
          disabled={simulationState.isActive}
        >
          <option value="">Select a route...</option>
          {SIMULATION_ROUTES.map((route) => (
            <option key={route.id} value={route.id}>
              {route.name} - {route.description}
            </option>
          ))}
        </select>
      </div>

      <div className="settings-group-header" style={{ marginTop: "32px" }}>
        Simulation Options
      </div>

      <SettingRow
        label="Add GPS Noise"
        description="Simulate realistic GPS accuracy variations"
        checked={simOptions.addNoise}
        onChange={(checked) => updateSimOption("addNoise", checked)}
      />

      {simOptions.addNoise && (
        <div className="slider-setting">
          <label>Noise Level: {simOptions.noiseLevel.toFixed(0)}m</label>
          <input
            type="range"
            min="1"
            max="20"
            step="1"
            value={simOptions.noiseLevel}
            onChange={(e) =>
              updateSimOption("noiseLevel", Number(e.target.value))
            }
          />
        </div>
      )}

      <SettingRow
        label="Vary Speed"
        description="Simulate realistic speed variations (hills, stops)"
        checked={simOptions.varySpeed}
        onChange={(checked) => updateSimOption("varySpeed", checked)}
      />

      {simOptions.varySpeed && (
        <div className="slider-setting">
          <label>
            Speed Variation: {(simOptions.speedVariation * 100).toFixed(0)}%
          </label>
          <input
            type="range"
            min="0"
            max="50"
            step="5"
            value={simOptions.speedVariation * 100}
            onChange={(e) =>
              updateSimOption("speedVariation", Number(e.target.value) / 100)
            }
          />
        </div>
      )}

      <SettingRow
        label="Generate Compass Data"
        description="Simulate compass/heading data"
        checked={simOptions.fakeCompass}
        onChange={(checked) => updateSimOption("fakeCompass", checked)}
      />

      <div className="slider-setting">
        <label>
          Speed Multiplier: {simOptions.speedMultiplier.toFixed(1)}x
        </label>
        <input
          type="range"
          min="0.5"
          max="10"
          step="0.5"
          value={simOptions.speedMultiplier}
          onChange={(e) =>
            updateSimOption("speedMultiplier", Number(e.target.value))
          }
        />
        <div
          style={{
            fontSize: "12px",
            color: "var(--text-secondary)",
            marginTop: "4px",
          }}
        >
          {simOptions.speedMultiplier < 1
            ? "Slower"
            : simOptions.speedMultiplier > 1
              ? "Faster"
              : "Normal speed"}
        </div>
      </div>

      <SettingRow
        label="Show Route on Map"
        description="Display the route line on the map with transparency"
        checked={simOptions.showRouteOnMap}
        onChange={(checked) => updateSimOption("showRouteOnMap", checked)}
      />

      <SettingRow
        label="Resume on Reload"
        description="Continue simulation from last position when page reloads"
        checked={userPreferences.resumeSimulationOnReload || false}
        onChange={(checked) =>
          updateUserPreferences({ resumeSimulationOnReload: checked })
        }
      />

      {/* Cache Management Section */}
      <div className="settings-group-header" style={{ marginTop: "32px" }}>
        Cache Management
      </div>
      <CacheManagementSection />

      {/* Clear All Data Section */}
      <div className="settings-group-header" style={{ marginTop: "32px" }}>
        Clear All Data
      </div>
      <div className="setting-group">
        <p className="setting-description">
          Clear all application data including localStorage, OPFS, IndexedDB,
          and caches. This will log you out, delete all activities, settings,
          and cached data. The app will reload after clearing.
        </p>
        <button
          className="control-btn danger"
          onClick={() => setShowClearAllConfirm(true)}
          style={{
            width: "100%",
            marginTop: "12px",
            border: "1px solid var(--danger)",
            color: "var(--danger)",
            background: "var(--bg-subtle)",
          }}
        >
          Clear All Data & Reload
        </button>
      </div>

      <ConfirmationDialog
        visible={showClearAllConfirm}
        title="Clear All Data?"
        message="This will log you out and permanently delete all activities, settings, cached data, and preferences. The app will reload and you'll start fresh. This action cannot be undone."
        confirmText="Clear All Data"
        cancelText="Cancel"
        isDangerous={true}
        onConfirm={() => {
          setShowClearAllConfirm(false);
          handleClearAllData();
        }}
        onCancel={() => setShowClearAllConfirm(false)}
      />
    </div>
  );
}
